/**
 * Scene Director Panel — manual beat-sequence editor.
 *
 * The LLM director (server-side, in `server/src/director.ts`) emits a
 * stream of Beats based on a prompt. This panel is the manual analogue:
 * a list-style UI where the user hand-authors a sequence of Beats with
 * the same controlled vocabulary the LLM has, then plays it through the
 * existing BeatPlayer.
 *
 * Why this exists:
 *   - Calibrating the LLM director without a fast manual loop is slow;
 *     every change requires a prompt round-trip.
 *   - Designing transitions (animation→hold→animation, idle→action,
 *     hold attack/release feels) requires being able to A/B short
 *     sequences in seconds, not minutes.
 *   - Anything we learn here translates directly to the LLM path
 *     because both producers emit the same Beat shape, route through
 *     the same player, and use the same vocabulary.
 *
 * Architecture notes:
 *   - Source surface is intentionally narrowed to `face_only` and
 *     `library` (Mixamo catalog clips). Uthana sources require server
 *     round-trips and credits and are out of scope for this manual UI.
 *   - Beat construction goes through `beat-utils.ts` which mirrors the
 *     server validator's `materialise*` functions. Both paths produce
 *     byte-equivalent Beat objects from equivalent inputs.
 *   - The LLM-driven DirectorClient and this panel are mutually
 *     exclusive at runtime: hitting Play here calls `director.stop()`
 *     to clear any active server session before draining our queue.
 *   - Beats are auto-saved to localStorage on every edit so refreshes
 *     don't lose the in-progress scene.
 */

import type { Figure } from '../motion/figure';
import type { BeatPlayer } from './beat-player';
import type { DirectorClient } from './director-client';
import type { Beat } from './types';
import {
  EMOTIONS,
  GAZE_DIRECTIONS,
  GESTURES,
  BODY_GESTURES,
  BODY_STATES,
  type Emotion,
  type GazeDirection,
  type Gesture,
  type BodyGesture,
  type BodyState,
} from './vocab';
import { ALL_PROCEDURAL_HOLDS } from '../motion/body-profiles';
import {
  materialiseBeat,
  cumulativeHoldState,
  type ManualBeatInput,
  type ClipMetadataLite,
} from './beat-utils';
import { tempoForEmotion } from './emotion-tempo';

// ── Storage ──────────────────────────────────────────────────────────────

const STORAGE_KEY = 'scene-director:beats:v1';

function loadBeats(): ManualBeatInput[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as ManualBeatInput[];
  } catch {
    return [];
  }
}

function saveBeats(beats: ManualBeatInput[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(beats));
  } catch {
    // localStorage might be unavailable (private mode); silently no-op.
  }
}

// ── Defaults ─────────────────────────────────────────────────────────────

function defaultBeat(): ManualBeatInput {
  return {
    duration_sec: 4,
    intensity: 0.6,
    energy: 0.5,
    emotion: 'neutral',
  };
}

// ── DOM helpers ──────────────────────────────────────────────────────────
//
// Tiny strongly-typed factories so the per-beat row builder stays readable.
// All elements are returned with classNames applied; the caller appends to
// wherever it likes.

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text != null) e.textContent = text;
  return e;
}

function makeSelect(
  options: ReadonlyArray<string>,
  current: string | undefined,
  noneLabel: string | null,
  onChange: (value: string) => void,
): HTMLSelectElement {
  const sel = el('select', 'sd-select');
  if (noneLabel != null) {
    const opt = el('option');
    opt.value = '';
    opt.textContent = noneLabel;
    sel.appendChild(opt);
  }
  for (const v of options) {
    const opt = el('option');
    opt.value = v;
    opt.textContent = v;
    sel.appendChild(opt);
  }
  sel.value = current ?? '';
  sel.addEventListener('change', () => onChange(sel.value));
  return sel;
}

/**
 * Format an emotion-driven default value as a placeholder string. The
 * materialiser uses this exact value when the corresponding field is
 * blank, so showing it in the input box gives the user a live preview
 * of what they're "agreeing to" by leaving the field empty.
 *
 * Format chosen to be glanceable: "auto: 1.4s" rather than the bare
 * number, so the user understands the field IS resolved (not zero) when
 * blank. Three significant digits matches the precision of the tempo
 * table (which is hand-authored to 1–2 dp).
 */
function autoLabel(seconds: number): string {
  return `auto: ${seconds.toFixed(seconds >= 10 ? 0 : seconds >= 1 ? 1 : 2)}s`;
}

function makeNumberInput(
  current: number | undefined,
  step: number,
  onChange: (n: number) => void,
  placeholder?: string,
): HTMLInputElement {
  const i = el('input', 'sd-num');
  i.type = 'number';
  i.step = String(step);
  if (current != null) i.value = String(current);
  if (placeholder) i.placeholder = placeholder;
  i.addEventListener('change', () => {
    const v = parseFloat(i.value);
    if (Number.isFinite(v)) onChange(v);
  });
  return i;
}

function makeRange(
  current: number,
  min: number,
  max: number,
  step: number,
  onChange: (n: number) => void,
): { input: HTMLInputElement; valueEl: HTMLSpanElement } {
  const input = el('input', 'sd-range');
  input.type = 'range';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(current);
  const valueEl = el('span', 'sd-range-value', current.toFixed(2));
  input.addEventListener('input', () => {
    const v = parseFloat(input.value);
    valueEl.textContent = v.toFixed(2);
    onChange(v);
  });
  return { input, valueEl };
}

// ── Public API ───────────────────────────────────────────────────────────

export interface SceneDirectorPanelOpts {
  figure: Figure;
  beatPlayer: BeatPlayer;
  /** Used to halt any active LLM session before draining manual queue. */
  director: DirectorClient;
  /** Server URL for the catalog endpoint. */
  serverUrl: string;
  /** URL the figure should revert to when playback stops. */
  idleClipUrl?: string;
  /** Mount point for the panel. Defaults to document.body. */
  container?: HTMLElement;
}

export interface SceneDirectorPanel {
  open(): void;
  close(): void;
  toggle(): void;
  destroy(): void;
  readonly isOpen: boolean;
}

export function createSceneDirectorPanel(
  opts: SceneDirectorPanelOpts,
): SceneDirectorPanel {
  const { figure, beatPlayer, director, serverUrl } = opts;
  const idleClipUrl = opts.idleClipUrl;
  const container = opts.container ?? document.body;

  let beats: ManualBeatInput[] = loadBeats();
  if (beats.length === 0) beats.push(defaultBeat());
  let isOpen = false;
  let isPlaying = false;
  /** Catalog map for library-beat materialisation. Lazy-loaded on open. */
  let catalog: Map<string, ClipMetadataLite> | null = null;
  let catalogLoading: Promise<void> | null = null;

  // ── Catalog fetch ──────────────────────────────────────────────────────
  async function ensureCatalog(): Promise<void> {
    if (catalog) return;
    if (catalogLoading) return catalogLoading;
    catalogLoading = (async () => {
      try {
        const res = await fetch(`${serverUrl}/catalog`);
        if (!res.ok) throw new Error(`catalog HTTP ${res.status}`);
        const data = (await res.json()) as { clips: ClipMetadataLite[] };
        catalog = new Map();
        for (const c of data.clips) catalog.set(c.name, c);
        // Re-render so library rows can populate their datalists.
        rebuildBeatList();
      } catch (err) {
        console.warn('[scene-director] catalog fetch failed', err);
        statusEl.textContent = `catalog failed: ${err instanceof Error ? err.message : err}`;
      }
    })();
    return catalogLoading;
  }

  // ── Root DOM ───────────────────────────────────────────────────────────
  const root = el('aside', 'scene-director-panel hidden');
  root.id = 'scene-director-panel';
  root.innerHTML = `
    <div class="sd-header">
      <span class="sd-title">Scene Director</span>
      <button class="sd-close" type="button" aria-label="Close">×</button>
    </div>

    <div class="sd-toolbar">
      <button class="sd-play" type="button">▶ Play</button>
      <button class="sd-stop" type="button">■ Stop</button>
      <button class="sd-restart" type="button" title="Stop and replay from beat 1">↻</button>
      <button class="sd-add" type="button">+ Beat</button>
      <button class="sd-clear" type="button">Clear</button>
      <span class="sd-status" aria-live="polite"></span>
    </div>

    <div class="sd-beat-list"></div>

    <div class="sd-footer">
      <button class="sd-copy" type="button" title="Copy scene as JSON">Copy JSON</button>
      <button class="sd-paste" type="button" title="Paste scene from clipboard">Paste JSON</button>
    </div>

    <datalist id="sd-clip-list"></datalist>
  `;
  container.appendChild(root);

  const closeBtn = root.querySelector('.sd-close') as HTMLButtonElement;
  const playBtn = root.querySelector('.sd-play') as HTMLButtonElement;
  const stopBtn = root.querySelector('.sd-stop') as HTMLButtonElement;
  const restartBtn = root.querySelector('.sd-restart') as HTMLButtonElement;
  const addBtn = root.querySelector('.sd-add') as HTMLButtonElement;
  const clearBtn = root.querySelector('.sd-clear') as HTMLButtonElement;
  const statusEl = root.querySelector('.sd-status') as HTMLSpanElement;
  const listEl = root.querySelector('.sd-beat-list') as HTMLDivElement;
  const copyBtn = root.querySelector('.sd-copy') as HTMLButtonElement;
  const pasteBtn = root.querySelector('.sd-paste') as HTMLButtonElement;
  const clipDatalist = root.querySelector('#sd-clip-list') as HTMLDataListElement;

  // ── Per-beat row builder ───────────────────────────────────────────────
  //
  // Each call rebuilds ONE row from the corresponding `beats[idx]`. We
  // rebuild rather than patch on edits because the cumulative-state
  // strip and the source-conditional rows depend on the whole list, and
  // the editor is small enough that O(n) rebuild on every change is
  // imperceptible. The whole list is re-rendered on structural edits
  // (add / remove / reorder).

  function buildBeatRow(idx: number): HTMLDivElement {
    const row = el('div', 'sd-beat');
    row.dataset['idx'] = String(idx);
    const b = beats[idx]!;

    // Resolved emotion-driven defaults — used as live-preview placeholders
    // on the blank smoothing / duration fields. When the user leaves a
    // field empty, the materialiser will use these values; surfacing them
    // here makes the auto-tempo visible without requiring the user to
    // play the beat first to find out. Re-computed every render so changes
    // to emotion / intensity update the placeholders immediately.
    const tempo = tempoForEmotion(b.emotion ?? 'neutral', b.intensity ?? 0.6);

    // Header: number, state strip, reorder + delete buttons
    const header = el('div', 'sd-beat-header');
    header.appendChild(el('span', 'sd-beat-num', String(idx + 1)));

    const stateStrip = el('span', 'sd-state-strip');
    const cum = cumulativeHoldState(beats, idx);
    const holdsList = [...cum.holds];
    const kind = b.clip ? 'library' : 'face-only';
    stateStrip.textContent =
      `${kind} · holds: ${holdsList.length > 0 ? holdsList.join(', ') : '(none)'}` +
      ` · state: ${cum.body_state}`;
    header.appendChild(stateStrip);

    const upBtn = el('button', 'sd-icon', '↑');
    upBtn.type = 'button';
    upBtn.disabled = idx === 0;
    upBtn.addEventListener('click', () => {
      if (idx === 0) return;
      [beats[idx - 1], beats[idx]] = [beats[idx]!, beats[idx - 1]!];
      onBeatsChanged();
    });
    const downBtn = el('button', 'sd-icon', '↓');
    downBtn.type = 'button';
    downBtn.disabled = idx === beats.length - 1;
    downBtn.addEventListener('click', () => {
      if (idx >= beats.length - 1) return;
      [beats[idx + 1], beats[idx]] = [beats[idx]!, beats[idx + 1]!];
      onBeatsChanged();
    });
    const dupBtn = el('button', 'sd-icon', '⧉');
    dupBtn.type = 'button';
    dupBtn.title = 'Duplicate beat';
    dupBtn.addEventListener('click', () => {
      beats.splice(idx + 1, 0, JSON.parse(JSON.stringify(b)) as ManualBeatInput);
      onBeatsChanged();
    });
    const delBtn = el('button', 'sd-icon sd-del', '×');
    delBtn.type = 'button';
    delBtn.title = 'Delete beat';
    delBtn.addEventListener('click', () => {
      beats.splice(idx, 1);
      if (beats.length === 0) beats.push(defaultBeat());
      onBeatsChanged();
    });
    header.append(upBtn, downBtn, dupBtn, delBtn);
    row.appendChild(header);

    // ── Primary row: emotion · duration ────────────────────────────────
    // No `source` dropdown — that was a false dichotomy. The presence
    // of a body clip below decides face_only vs library at materialise
    // time; every other field applies regardless.
    const r1 = el('div', 'sd-row');
    r1.append(
      labelled('emotion',
        makeSelect(
          EMOTIONS,
          b.emotion ?? 'neutral',
          null,
          (v) => { b.emotion = v as Emotion; onBeatsChanged(); },
        ),
      ),
      labelled('duration',
        // Live-preview placeholder: the resolved duration_sec the
        // materialiser will use when this field is blank, computed from
        // the beat's emotion + intensity. Surprise at intensity=1.0 →
        // "auto: 2.5s"; sadness at 0.6 → "auto: ~9.4s". Re-rendered on
        // every beat change because emotion / intensity drive it.
        makeNumberInput(b.duration_sec ?? undefined, 0.5, (n) => {
          b.duration_sec = n;
          onBeatsChanged();
        }, autoLabel(tempo.duration_sec)),
      ),
    );
    row.appendChild(r1);

    // ── Optional body clip ─────────────────────────────────────────────
    // Always visible. Empty = face_only beat (no body clip change).
    // Filled = library beat that plays the named clip; the user's
    // emotion / holds / state still apply on top.
    const r2 = el('div', 'sd-row');
    const clipInput = el('input', 'sd-clip');
    clipInput.type = 'text';
    clipInput.setAttribute('list', 'sd-clip-list');
    clipInput.placeholder = catalog
      ? 'optional — leave blank for face-only beat'
      : 'loading catalog…';
    clipInput.value = b.clip ?? '';
    clipInput.addEventListener('change', () => {
      b.clip = clipInput.value.trim() || undefined;
      onBeatsChanged();
    });
    const meta = b.clip ? catalog?.get(b.clip) : undefined;
    const metaSpan = el('span', 'sd-clip-meta');
    if (meta) {
      const tags = [
        meta.loopable ? 'loopable' : 'one-shot',
        meta.is_idle ? 'idle' : null,
        meta.symmetric ? 'sym' : null,
        `catalog emotion=${meta.emotion}`,
      ].filter(Boolean);
      metaSpan.textContent = tags.join(' · ');
    } else if (b.clip) {
      metaSpan.textContent = '(unknown clip)';
      metaSpan.classList.add('sd-warn');
    } else {
      metaSpan.textContent = '(face-only)';
    }
    r2.append(labelled('body clip', clipInput), metaSpan);
    row.appendChild(r2);

    // ── Intensity / energy sliders ─────────────────────────────────────
    //
    // Live updates use scheduleSave (cheap, no rebuild); on release
    // (`change` event) we also onBeatsChanged so the smoothing/duration
    // placeholders refresh — they're computed from emotion+intensity
    // via tempoForEmotion. The slider uses `input` for live tracking, so
    // we attach a separate `change` listener here for the release-time
    // rebuild.
    const r3 = el('div', 'sd-row sd-sliders');
    {
      const i = makeRange(b.intensity ?? 0.6, 0, 1, 0.01, (v) => {
        b.intensity = v;
        scheduleSave();
      });
      i.input.addEventListener('change', () => onBeatsChanged());
      r3.append(labelled('intensity', wrapPair(i.input, i.valueEl)));
    }
    {
      const e = makeRange(b.energy ?? 0.5, 0, 1, 0.01, (v) => {
        b.energy = v;
        scheduleSave();
      });
      r3.append(labelled('energy', wrapPair(e.input, e.valueEl)));
    }
    row.appendChild(r3);

    // ── Procedural channels: gaze / face gesture / body gesture / state ─
    const r4 = el('div', 'sd-row');
    r4.append(
      labelled('gaze',
        makeSelect(GAZE_DIRECTIONS, b.gaze ?? '', '(auto)',
          (v) => { b.gaze = (v || null) as GazeDirection | null; onBeatsChanged(); },
        ),
      ),
      labelled('face gesture',
        makeSelect(GESTURES, b.gesture ?? '', '(none)',
          (v) => { b.gesture = (v || null) as Gesture | null; onBeatsChanged(); },
        ),
      ),
    );
    row.appendChild(r4);

    const r5 = el('div', 'sd-row');
    r5.append(
      labelled('body gesture',
        makeSelect(BODY_GESTURES, b.body_gesture ?? '', '(none)',
          (v) => { b.body_gesture = (v || null) as BodyGesture | null; onBeatsChanged(); },
        ),
      ),
      labelled('body state',
        makeSelect(BODY_STATES, b.body_state ?? '', '(inherit)',
          (v) => { b.body_state = (v || null) as BodyState | null; onBeatsChanged(); },
        ),
      ),
    );
    row.appendChild(r5);

    // ── Holds: activate + release ──────────────────────────────────────
    //
    // Single-select on each side: a beat activates AT MOST one hold and
    // releases AT MOST one hold. This mirrors the principle that the
    // body plays one clip at a time — it should also wear one posture
    // at a time. To compose "both arms doing different things", use the
    // dedicated combination poses (e.g. `hands_on_hips` rather than
    // pairing `hand_on_hip_left` + `hand_on_hip_right`).
    //
    // The release dropdown is filtered to ONLY the holds currently
    // active going into this beat (computed by cumulativeHoldState
    // from beats[0..idx-1]). When nothing is active, the dropdown is
    // disabled so you literally cannot ask the runtime to release a
    // hold that isn't on — a class of confusion this UI used to invite.
    //
    // The schema fields stay as arrays (the LLM path is unchanged); the
    // UI just emits 0-or-1-element arrays. Storage compatibility with
    // older saved scenes is preserved by sanitisation below.

    // Sanitise stale state before rendering: if an earlier edit
    // produced multi-element arrays, collapse to a single value. If the
    // current release reference points at a hold that's no longer
    // active going into this beat (e.g. you reordered earlier beats),
    // drop it.
    const incomingActive = cumulativeHoldState(beats, idx);
    const activeNames = [...incomingActive.holds];
    if (b.holds && b.holds.length > 1) b.holds = b.holds.slice(0, 1);
    if (b.release_holds && b.release_holds.length > 1) {
      b.release_holds = b.release_holds.slice(0, 1);
    }
    if (b.release_holds && b.release_holds[0] && !activeNames.includes(b.release_holds[0])) {
      b.release_holds = undefined;
    }

    const r6 = el('div', 'sd-row sd-holds-row');

    const holdSelect = makeSelect(
      ALL_PROCEDURAL_HOLDS,
      b.holds?.[0] ?? '',
      '(none)',
      (v) => {
        b.holds = v ? [v] : undefined;
        onBeatsChanged();
      },
    );

    let releaseControl: HTMLElement;
    if (activeNames.length === 0) {
      // No active holds going into this beat. Render a disabled select
      // so the row keeps the same visual rhythm as other beats and the
      // user gets an explicit "(no active holds to release)" cue rather
      // than a confusing empty dropdown.
      const disabled = el('select', 'sd-select');
      const opt = el('option');
      opt.value = '';
      opt.textContent = '(no active holds to release)';
      disabled.appendChild(opt);
      disabled.disabled = true;
      releaseControl = disabled;
    } else {
      releaseControl = makeSelect(
        activeNames,
        b.release_holds?.[0] ?? '',
        '(none)',
        (v) => {
          b.release_holds = v ? [v] : undefined;
          onBeatsChanged();
        },
      );
    }

    r6.append(
      labelled('+ hold', holdSelect),
      labelled('− release', releaseControl),
    );
    row.appendChild(r6);

    // ── Smoothing controls (collapsible) ───────────────────────────────
    //
    // Each blank field shows the emotion-driven default in its placeholder
    // (see `autoLabel`). Leave blank to inherit; type a number to override.
    // The summary row also surfaces the easing family so the user can see
    // *which curve* the beat will run through (gentle / smooth / snap /
    // jolt) without having to remember the per-emotion mapping.
    const adv = el('details', 'sd-advanced') as HTMLDetailsElement;
    adv.open = b.fade_sec != null
      || b.hold_attack_sec != null
      || b.hold_release_sec != null;
    adv.appendChild(
      el('summary', undefined, `Smoothing & flow · easing: ${tempo.easing}`),
    );

    const advRow = el('div', 'sd-row');
    advRow.append(
      labelled('fade_sec',
        makeNumberInput(b.fade_sec, 0.05, (n) => { b.fade_sec = n; scheduleSave(); }, autoLabel(tempo.fade_sec)),
      ),
      labelled('hold attack',
        makeNumberInput(b.hold_attack_sec, 0.05, (n) => { b.hold_attack_sec = n; scheduleSave(); }, autoLabel(tempo.hold_attack_sec)),
      ),
      labelled('hold release',
        makeNumberInput(b.hold_release_sec, 0.05, (n) => { b.hold_release_sec = n; scheduleSave(); }, autoLabel(tempo.hold_release_sec)),
      ),
    );
    adv.appendChild(advRow);
    row.appendChild(adv);

    // ── Reason ─────────────────────────────────────────────────────────
    const reasonRow = el('div', 'sd-row');
    const reasonInput = el('input', 'sd-reason');
    reasonInput.type = 'text';
    reasonInput.placeholder = 'reason (optional, shown in HUD)';
    reasonInput.value = b.reason ?? '';
    reasonInput.addEventListener('change', () => {
      b.reason = reasonInput.value;
      scheduleSave();
    });
    reasonRow.append(labelled('reason', reasonInput));
    row.appendChild(reasonRow);

    return row;
  }

  // Helper for `<label>field</label> <control>` layout used everywhere.
  function labelled(label: string, control: HTMLElement): HTMLDivElement {
    const wrap = el('div', 'sd-field');
    wrap.append(el('span', 'sd-field-label', label), control);
    return wrap;
  }

  function wrapPair(a: HTMLElement, b: HTMLElement): HTMLDivElement {
    const wrap = el('div', 'sd-pair');
    wrap.append(a, b);
    return wrap;
  }

  // ── State sync ────────────────────────────────────────────────────────
  //
  // `onBeatsChanged` is the single rebuild + persist site. Called after
  // any structural edit (add / remove / reorder / source-switch / hold
  // change) so the cumulative-state strip and source-conditional rows
  // stay accurate. For pure scalar changes (sliders / numbers / reason)
  // we use scheduleSave() instead — no rebuild, just persist.

  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleSave(): void {
    if (saveTimer != null) return;
    saveTimer = setTimeout(() => {
      saveTimer = null;
      saveBeats(beats);
    }, 250);
  }
  function flushSave(): void {
    if (saveTimer != null) {
      clearTimeout(saveTimer);
      saveTimer = null;
    }
    saveBeats(beats);
  }
  function onBeatsChanged(): void {
    flushSave();
    rebuildBeatList();
  }

  function rebuildBeatList(): void {
    listEl.innerHTML = '';
    for (let i = 0; i < beats.length; i += 1) {
      listEl.appendChild(buildBeatRow(i));
    }
    // Refresh the shared datalist: it's keyed by `id="sd-clip-list"` so
    // every per-beat <input list="sd-clip-list"> sees the same options.
    refreshClipDatalist();
  }

  function refreshClipDatalist(): void {
    if (!catalog) return;
    if (clipDatalist.childElementCount > 0) return; // populated once
    const frag = document.createDocumentFragment();
    for (const name of catalog.keys()) {
      const opt = el('option');
      opt.value = name;
      frag.appendChild(opt);
    }
    clipDatalist.appendChild(frag);
  }

  // ── Toolbar wiring ────────────────────────────────────────────────────
  addBtn.addEventListener('click', () => {
    beats.push(defaultBeat());
    onBeatsChanged();
  });

  clearBtn.addEventListener('click', () => {
    if (!confirm('Clear all beats?')) return;
    beats = [defaultBeat()];
    onBeatsChanged();
  });

  closeBtn.addEventListener('click', () => close());

  playBtn.addEventListener('click', () => {
    void play();
  });
  stopBtn.addEventListener('click', () => {
    stop();
  });
  restartBtn.addEventListener('click', () => {
    stop();
    void play();
  });

  copyBtn.addEventListener('click', async () => {
    const text = JSON.stringify(beats, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      statusEl.textContent = `Copied ${beats.length} beat(s) as JSON`;
    } catch {
      console.log('[scene-director] beats:\n' + text);
      statusEl.textContent = 'Clipboard unavailable — see console';
    }
  });

  pasteBtn.addEventListener('click', async () => {
    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      const fallback = prompt('Paste scene JSON:');
      if (!fallback) return;
      text = fallback;
    }
    try {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error('expected array');
      beats = parsed as ManualBeatInput[];
      if (beats.length === 0) beats = [defaultBeat()];
      onBeatsChanged();
      statusEl.textContent = `Loaded ${beats.length} beat(s)`;
    } catch (err) {
      statusEl.textContent = `Paste failed: ${err instanceof Error ? err.message : err}`;
    }
  });

  // ── Playback ──────────────────────────────────────────────────────────
  //
  // play(): halt any in-flight director session, materialise each manual
  // beat into a runtime Beat (looking up library clip metadata from the
  // cached catalog), then enqueue + start the BeatPlayer. Errors during
  // materialisation surface in the status line and the bad beat is
  // skipped so the rest of the scene can still play.
  async function play(): Promise<void> {
    if (beats.length === 0) {
      statusEl.textContent = 'no beats to play';
      return;
    }
    await ensureCatalog();
    director.stop(); // also resets the BeatPlayer
    beatPlayer.reset();

    const lookup = (name: string) => catalog?.get(name);
    const realised: Beat[] = [];
    const errors: string[] = [];
    for (let i = 0; i < beats.length; i += 1) {
      try {
        realised.push(materialiseBeat(beats[i]!, lookup));
      } catch (err) {
        errors.push(`beat ${i + 1}: ${err instanceof Error ? err.message : err}`);
      }
    }
    if (realised.length === 0) {
      statusEl.textContent = `nothing playable (${errors.length} error${errors.length === 1 ? '' : 's'})`;
      console.warn('[scene-director] all beats failed to materialise:', errors);
      return;
    }
    if (errors.length > 0) {
      console.warn('[scene-director] some beats skipped:', errors);
    }

    beatPlayer.enqueue(realised);
    beatPlayer.start();
    isPlaying = true;
    statusEl.textContent = `playing ${realised.length} beat(s)…${errors.length > 0 ? ` (${errors.length} skipped)` : ''}`;
    setPlayingClass();
  }

  function stop(): void {
    beatPlayer.stop();
    if (idleClipUrl) {
      figure.playClipFromUrl(idleClipUrl, 0.3).catch((err) =>
        console.warn('[scene-director] revertToIdle failed', err),
      );
    }
    isPlaying = false;
    statusEl.textContent = 'stopped';
    setPlayingClass();
  }

  function setPlayingClass(): void {
    root.classList.toggle('sd-playing', isPlaying);
  }

  // ── Open / close lifecycle ────────────────────────────────────────────
  function open(): void {
    if (isOpen) return;
    isOpen = true;
    root.classList.remove('hidden');
    rebuildBeatList();
    void ensureCatalog();
  }

  function close(): void {
    if (!isOpen) return;
    isOpen = false;
    if (isPlaying) stop();
    root.classList.add('hidden');
  }

  function toggle(): void {
    if (isOpen) close(); else open();
  }

  return {
    open,
    close,
    toggle,
    destroy(): void { close(); root.remove(); },
    get isOpen(): boolean { return isOpen; },
  };
}
